import React, { useContext, useState, type ContextType } from 'react';
import { useIntl } from 'react-intl';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
} from '../../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../../hooks/analytics/useSnippetAnalytics.ts';
import { useIsEditorViewMode } from '../../hooks/panel/useIsEditorViewMode.ts';
import { useInsertSnippet } from '../../hooks/panel/useInsertSnippet.ts';
import type { Snippet } from '../../types/snippet.ts';
import { SnippetPreview } from '../../preview/SnippetPreview.tsx';
import { SnippetPreviewSkeleton } from '../../preview/skeleton/SnippetPreviewSkeleton.tsx';
import { useSnippetsActions } from '../../store/snippetsStore.ts';
import { useSnippetPopupPreview } from '../preview/SnippetPopupPreviewContext.tsx';
import {
	SNIPPET_PREVIEW_CONTAINER,
	SnippetPreviewContainer,
} from '../preview/SnippetPreviewContainer.tsx';

import { SnippetPanelCard } from './SnippetPanelCard.tsx';
import { messages } from './SnippetPanelItem.i18n.ts';
import { SnippetPanelItemContent } from './SnippetPanelItemContent.tsx';

const EDITOR_PRE_SCALE_WIDTH = 800;

type SnippetPanelItemProps = {
	snippet: Snippet;
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelItem = ({
	snippet,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelItemProps): React.JSX.Element => {
	const { userId } = useSessionData();
	const { editorAPI } = useContext(EditorContextPanelContext) as ContextType<
		typeof EditorContextPanelContext
	>;

	const intl = useIntl();

	const [isHovered, setIsHovered] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const isViewMode = useIsEditorViewMode();
	const insertSnippet = useInsertSnippet(editorAPI);
	const snippetsActions = useSnippetsActions();
	const { fireButtonClickedAnalytic, fireFailedAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.SNIPPET_PANEL,
	);
	const { showPreview, hidePreview } = useSnippetPopupPreview();

	const handleMouseEnter = (event: React.MouseEvent) => {
		setIsHovered(true);
		showPreview(snippet, event.currentTarget.getBoundingClientRect());
	};
	const handleMouseLeave = () => setIsHovered(false);
	const handleMenuOpenChange = (isOpen: boolean) => setIsMenuOpen(isOpen);
	const onClick = () => {
		if (isViewMode) {
			return;
		}
		const result = insertSnippet(snippet, true);
		if (result.status === 'success') {
			hidePreview();
			snippetsActions.recordSnippetInsert(snippet.snippetId);
			fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.INSERT_SNIPPET, {
				userId: userId ?? undefined,
				scope: snippet.scope,
				snippetId: snippet.snippetId,
				isShared: snippet.isShared,
				templateType: snippet.templateType,
				timestamp: Date.now(),
			});
		} else if (result.status === 'error' && fg('platform_editor_blocks_patch_5')) {
			// Only genuine insert exceptions are reported as failures, with a
			// diagnostic reason. Benign not-ready cases (editor API unavailable,
			// empty body) are intentionally not fired so they don't inflate the
			// insert error-rate metric.
			//
			// Emitted as `snippetApiInsert / failed` for naming consistency with
			// the other snippetApi(all ops) operational events (create/update/
			// fetch). The old operational `insertSnippet / failed` event has been
			// retired; this renamed event ships behind platform_editor_blocks_patch_5
			// (the current active blocks patch batch).
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_INSERT, {
				userId: userId ?? undefined,
				scope: snippet.scope,
				snippetId: snippet.snippetId,
				isShared: snippet.isShared,
				templateType: snippet.templateType,
				reason: result.reason,
				timestamp: Date.now(),
			});
		}
	};

	const showActions = isHovered || snippet.isFavorite || isMenuOpen;
	const showPanelActions = isHovered || isMenuOpen;

	return (
		<SnippetPanelCard
			isActive={isMenuOpen}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={onClick}
			isDisabled={isViewMode}
			ariaLabel={intl.formatMessage(messages.snippetCardAriaLabel, { name: snippet.name })}
			testId={`snippet-card-button-${snippet.snippetId}`}
		>
			<SnippetPreviewContainer>
				<SnippetPreview
					snippet={snippet}
					dimensions={{
						width: `calc(${SNIPPET_PREVIEW_CONTAINER.width}px - (${SNIPPET_PREVIEW_CONTAINER.paddingInline} * 2))`,
						maxHeight: `calc(${SNIPPET_PREVIEW_CONTAINER.height}px - (${SNIPPET_PREVIEW_CONTAINER.paddingBlock} * 2))`,
					}}
					renderWidth={EDITOR_PRE_SCALE_WIDTH}
					loadingComponent={<SnippetPreviewSkeleton />}
				/>
			</SnippetPreviewContainer>
			<SnippetPanelItemContent
				snippet={snippet}
				isHovered={isHovered}
				showActions={showActions}
				showPanelActions={showPanelActions}
				onMenuOpenChange={handleMenuOpenChange}
				contentId={contentId}
				spaceId={spaceId}
				spaceKey={spaceKey}
			/>
		</SnippetPanelCard>
	);
};
